import React, { useRef, useEffect, useState } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { TextField, Popper, FormHelperText } from '@material-ui/core';
import { useInput, useTranslate, useDataProvider } from 'react-admin';
import { required } from 'ra-core';

interface Choice {
  id: string | number;
  [key: string]: any;
}

interface StableAutocompleteInputProps {
  choices?: Choice[];
  reference?: string;
  perPage?: number;
  source: string;
  optionText?: string | ((record: Choice) => string);
  label?: string;
  helperText?: string;
  variant?: 'outlined' | 'filled' | 'standard';
  margin?: 'dense' | 'none' | 'normal';
  isRequired?: boolean;
  currentPerson?: any;
  validate?: any;
  setFilter?: (value: string) => void;
}

const StableAutocompleteInput: React.FC<StableAutocompleteInputProps> = ({
  source,
  choices,
  reference,
  optionText = 'name',
  label,
  helperText,
  variant = 'outlined',
  margin = 'dense',
  validate,
  isRequired,
  currentPerson,
  perPage = 25,
  setFilter,
  ...rest
}) => {
  const [currentPersonId, setCurrentPersonId] = useState<string | number | null>(null);
  const [fetchedChoices, setFetchedChoices] = useState<Choice[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const dataProvider = useDataProvider();

  const isFetchMode = !!reference;
  const finalChoices = isFetchMode ? fetchedChoices : (choices || []);

  useEffect(() => {
    if (currentPerson && typeof currentPerson === 'object' && currentPerson.id) {
      setCurrentPersonId(currentPerson.id);
    } else {
      setCurrentPersonId(currentPerson);
    }
  }, [currentPerson]);

  const translate = useTranslate();
  const { input, meta } = useInput({
    source,
    validate: isRequired ? [required()] : validate,
    ...rest,
  });

  const anchorEl = useRef<HTMLDivElement | null>(null);
  const [menuWidth, setMenuWidth] = useState<string>('auto');

  useEffect(() => {
    if (anchorEl.current) {
      setMenuWidth(`${anchorEl.current.clientWidth}px`);
    }
  }, []);

  useEffect(() => {
    if (!isFetchMode) return;

    let active = true;
    dataProvider
      .getList(reference!, {
        pagination: { page: 1, perPage },
        sort: { field: 'id', order: 'ASC' },
        filter: {},
      })
      .then(({ data }) => {
        if (active) {
          setFetchedChoices(data);
        }
      })
      .catch(() => {
        if (active) {
          setFetchedChoices([]);
        }
      });
    return () => {
      active = false;
    };
  }, [dataProvider, reference, perPage, isFetchMode]);

  useEffect(() => {
    if (!isFetchMode || !searchText) return;

    let active = true;
    dataProvider
      .getList(reference!, {
        pagination: { page: 1, perPage },
        sort: { field: 'id', order: 'ASC' },
        filter: { q: searchText },
      })
      .then(({ data }) => {
        if (active) {
          setFetchedChoices(data);
        }
      })
      .catch(() => {
        if (active) {
          setFetchedChoices([]);
        }
      });
    return () => {
      active = false;
    };
  }, [searchText, dataProvider, reference, perPage, isFetchMode]);

  const getOptionLabel = (option: Choice) => {
    if (!option) return '';
    if (typeof optionText === 'function') {
      return optionText(option);
    }
    return option[optionText] || '';
  };

  const validateNotSelf = (value: Choice | null) => {
    if (value?.id === currentPersonId) {
      return 'No puede relacionarse consigo mismo';
    }
    return undefined;
  };

  const CustomPopper = (props: any) => (
    <Popper
      {...props}
      style={{ width: menuWidth, zIndex: 9999 }}
      placement="bottom-start"
      modifiers={{
        flip: { enabled: false },
        preventOverflow: { enabled: false },
      }}
    />
  );

  const showError = meta.touched && (!!meta.error || input.value === currentPersonId);
  const defaultHelperText = isFetchMode
    ? 'Los resultados de la búsqueda están sujetos al tipo de persona seleccionada, adicional la persona debe estar previamente creada en el sistema.'
    : 'Seleccione una opción de la lista';

  const filteredChoices = finalChoices.filter(choice => choice.id !== currentPersonId);

  return (
    <div ref={anchorEl}>
      <Autocomplete
        options={filteredChoices}
        getOptionLabel={getOptionLabel}
        value={finalChoices.find(c => c.id === input.value) || null}
        onChange={(_, value) => {
          const error = validateNotSelf(value);
          if (!error) {
            input.onChange(value?.id ?? null);
          }
        }}
        onBlur={input.onBlur}
        PopperComponent={CustomPopper}
        onInputChange={(_, value, reason) => {
          if (reason === 'clear') {
            input.onChange(null);
            if (isFetchMode) {
              setSearchText('');
            } else if (setFilter) {
              setFilter('');
            }
          } else if (reason === 'input') {
            if (isFetchMode) {
              setSearchText(value);
            } else if (setFilter) {
              setFilter(value);
            }
          }
        }}
        renderInput={(params) => (
          <div>
            <TextField
              {...params}
              label={
                <>
                  {label ? translate(label) : ''}
                  {isRequired && <span> *</span>}
                </>
              }
              variant={variant}
              margin={margin}
              error={showError}
              fullWidth
            />
            <FormHelperText
              error={showError}
              style={{ fontSize: '0.75rem', marginTop: 4 }}
            >
              {input.value === currentPersonId
                ? 'No puede relacionarse consigo mismo'
                : meta.error
                  ? (typeof meta.error === 'string' ? translate(meta.error) : meta.error)
                  : helperText || defaultHelperText}
            </FormHelperText>
          </div>
        )}
      />
    </div>
  );
};

export default StableAutocompleteInput;